import { useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";

import { SafeLink } from "~/components/ui/safe-link";
import { cn } from "~/lib/cn";

/**
 * Renders a CommonMark string to React. Server-rendered (no editor mount, no
 * client-only effects), so the prose lands in the SSR HTML and is crawlable.
 *
 * Links go through the platform link policy via SafeLink. Pass `ugc` for
 * coach-authored copy (profile sections, offers, resources) so outbound links
 * carry `rel="ugc"`; leave it off for editorial copy (category/city pages).
 *
 * Aliases: markdown renderer, prose, rich text, article copy.
 */
export const MarkdownContent = (props: {
  content: string | null | undefined;
  className?: string;
  /** Mark outbound links as user-generated (coach-authored). Default editorial. */
  ugc?: boolean;
  /**
   * Render markdown `#` headings as <h2>. Use when the page template already
   * renders its own <h1> so the document keeps a single H1.
   */
  demoteH1?: boolean;
}) => {
  const ugc = props.ugc ?? false;
  const demoteH1 = props.demoteH1 ?? false;

  const components = useMemo<Components>(
    () => ({
      a: ({ href, title, children }) => (
        <SafeLink href={href ?? ""} ugc={ugc} title={title}>
          {children}
        </SafeLink>
      ),
      ...(demoteH1
        ? {
            h1: ({ children }) => <h2>{children}</h2>,
          }
        : {}),
    }),
    [ugc, demoteH1],
  );

  if (!props.content) return null;

  return (
    <div className={cn("prose", props.className)}>
      <ReactMarkdown components={components}>{props.content}</ReactMarkdown>
    </div>
  );
};
